'use client';

import { jsx as _jsx } from "react/jsx-runtime";
/**
 * `SdkImpersonationProvider` — web-platform impersonation provider (spec 048).
 *
 * Accepts the impersonation store state as props (from the app-level Zustand
 * store), wires all SDK-owned concerns (hydration check, TTL monitor, API
 * calls, overlay context), and exposes `useImpersonation()` to the tree.
 *
 * The Zustand store itself stays in the frontend. The SDK only receives
 * the state snapshot + mutator callbacks so it has no Zustand dependency.
 *
 * App-specific side-effects (toast, telemetry/analytics callbacks) are
 * injected as optional callback props — not embedded.
 *
 * Web-only: lives in `src/platform/web/auth/`.
 */
import { useCallback, useMemo } from 'react';
import { ImpersonationOverlayProvider } from '../../../../auth/impersonation/auth-overlay-context.js';
import { ImpersonationProvider } from '../../../../auth/impersonation/context.js';
import { startTtlMonitor } from '../../../../auth/impersonation/ttl-monitor.js';
import { GrantApiClientError, getGrantStatus, mintGrant, resolveTarget as resolveTargetApi } from './impersonation-api-client.js';
import { teardownImpersonation } from './impersonation-teardown.js';
import { ImpersonationContext } from './use-impersonation.js';
// ── hashUserId ────────────────────────────────────────────────────────────────
/** Compute a 12-char hex prefix of `SHA-256(value)` for analytics correlation. */
async function hashUserId(value) {
  if (typeof globalThis.crypto !== 'undefined' && typeof globalThis.crypto.subtle !== 'undefined') {
    const data = new TextEncoder().encode(value);
    const digest = await globalThis.crypto.subtle.digest('SHA-256', data);
    const bytes = new Uint8Array(digest);
    let hex = '';
    for (let i = 0; i < bytes.length; i++) {
      const byte = bytes[i];
      /* v8 ignore start -- defensive: `bytes[i]` over a Uint8Array with `i < bytes.length` is always a number; the guard exists only for TS noUncheckedIndexedAccess narrowing and is unreachable at runtime. */
      if (byte === undefined) continue;
      /* v8 ignore stop */
      hex += byte.toString(16).padStart(2, '0');
    }
    return hex.slice(0, 12);
  }
  let h = 0;
  for (let i = 0; i < value.length; i++) {
    h = (h << 5) - h + value.charCodeAt(i) | 0;
  }
  return Math.abs(h).toString(16).padStart(12, '0').slice(0, 12);
}
/** Read the engineer's user id from `morfi_user_id` in localStorage. */
function readEngineerUserIdHint() {
  /* v8 ignore start -- defensive: this web-platform-only hook runs only inside a browser document; `localStorage` is always defined there (and in jsdom), so the no-localStorage arm is unreachable. */
  if (typeof localStorage === 'undefined') return '';
  /* v8 ignore stop */
  try {
    return localStorage.getItem('morfi_user_id') ?? '';
  } catch {
    // SILENT-CATCH: localStorage may be blocked by privacy mode.
    return '';
  }
}
// ── Provider ──────────────────────────────────────────────────────────────────
export function SdkImpersonationProvider({
  children,
  state,
  mutators,
  onExpiredToast,
  onRevokeError
}) {
  // Hand-tuned `useCallback` dependency arrays (e.g. `mutators.setState`
  // rather than the whole `mutators` object) are intentionally narrower than
  // the React Compiler would infer; it cannot preserve that manual
  // memoization. Opt out to keep the author's precise dependencies.
  'use no memo';

  const overlay = {
    isActive: state.isActive,
    target: state.target
  };
  const getTeardownState = useCallback(() => ({
    isActive: state.isActive,
    grantToken: state.grantToken,
    network: state.network,
    abortController: state.abortController
  }), [state.isActive, state.grantToken, state.network, state.abortController]);
  const doTeardown = useCallback(async reason => {
    const cbs = {
      onSetInactive: mutators.setInactive
    };
    if (onExpiredToast !== undefined) cbs.onExpiredToast = onExpiredToast;
    if (onRevokeError !== undefined) cbs.onRevokeError = onRevokeError;
    await teardownImpersonation(getTeardownState(), cbs, reason);
  }, [getTeardownState, mutators.setInactive, onExpiredToast, onRevokeError]);
  // ── Hydration ──────────────────────────────────────────────────────────────
  const onHydrate = useCallback(() => {
    if (!state.isActive || state.grantToken === null || state.network === null) return;
    const tokenAtMount = state.grantToken;
    void (async () => {
      try {
        const status = await getGrantStatus(tokenAtMount);
        if (status === null) {
          await doTeardown('invalidated');
          return;
        }
        const expiresAtMs = new Date(status.expires_at).getTime();
        const lastUsedMs = new Date(status.last_used_at).getTime();
        mutators.setState({
          expiresAt: expiresAtMs,
          lastUsedAt: lastUsedMs
        });
      } catch (err) {
        // SILENT-CATCH: hydration probe — only definitive backend rejection tears down.
        if (err instanceof GrantApiClientError) {
          if (err.code === 'IMPERSONATION_GRANT_EXPIRED' || err.code === 'IMPERSONATION_GRANT_INVALID') {
            await doTeardown('invalidated');
            return;
          }
        }
        // Network errors / 5xx — leave persisted state alone.
      }
    })();
    // `ImpersonationProvider` invokes `onHydrate` exactly once on mount (its
    // own module-level guard); the identity of this callback changing across
    // renders is irrelevant. Deps are listed for lint correctness only.
  }, [state.isActive, state.grantToken, state.network, mutators.setState, doTeardown]);
  // ── TTL monitor ────────────────────────────────────────────────────────────
  const startMonitor = useCallback(() => {
    if (!state.isActive || state.expiresAt === null || state.grantToken === null) {
      return () => {};
    }
    return startTtlMonitor({
      expiresAt: state.expiresAt,
      fetchGrantStatus: async () => {
        // Read live token — may have changed since monitor started.
        if (!state.isActive || state.grantToken === null) {
          const err_0 = new Error('Grant ended');
          err_0.code = 'IMPERSONATION_GRANT_INVALID';
          throw err_0;
        }
        const status_0 = await getGrantStatus(state.grantToken);
        if (status_0 === null) {
          const err_1 = new Error('Grant no longer valid');
          err_1.code = 'IMPERSONATION_GRANT_INVALID';
          throw err_1;
        }
        return status_0;
      },
      onExpired: async reason_0 => {
        await doTeardown(reason_0);
      },
      onStatusRefreshed: (expiresAtMs_0, lastUsedMs_0) => {
        mutators.setState({
          expiresAt: expiresAtMs_0,
          lastUsedAt: lastUsedMs_0
        });
      }
    });
  }, [state.isActive, state.expiresAt, state.grantToken, doTeardown, mutators]);
  // ── Actions ────────────────────────────────────────────────────────────────
  const activate = useCallback(async input => {
    const response = await mintGrant({
      target_identifier: input.target_identifier,
      ...(input.reason !== undefined ? {
        reason: input.reason
      } : {})
    });
    const issuedAt = Date.now();
    const expiresAtMs_1 = new Date(response.expires_at).getTime();
    const targetUserIdHash = await hashUserId(response.target.user_id);
    const engineerUserId = readEngineerUserIdHint();
    const normalizedTarget = {
      userId: response.target.user_id,
      displayName: response.target.display_name,
      handle: response.target.handle,
      primaryWallet: response.target.primary_wallet,
      avatarUrl: response.target.avatar_url,
      avatarNsfw: response.target.avatar_nsfw === true,
      heroUrl: response.target.hero_url ?? null,
      heroNsfw: response.target.hero_nsfw === true,
      connectedProviders: response.target.connected_providers.map(p => ({
        provider: p.provider,
        username: p.username,
        displayName: p.display_name
      })),
      walletAddresses: response.target.primary_wallet !== null ? [response.target.primary_wallet] : []
    };
    const payload = {
      grantToken: response.grant_token,
      grantId: response.grant_id,
      network: response.network,
      target: normalizedTarget,
      issuedAt,
      expiresAt: expiresAtMs_1,
      idleTtlSeconds: response.config.idle_ttl_seconds,
      targetUserIdHash,
      engineerUserId
    };
    mutators.setActive(payload);
  }, [mutators]);
  const stop = useCallback(async () => {
    await doTeardown('engineer_stop');
  }, [doTeardown]);
  const resolveTarget = useCallback(async input_0 => {
    return resolveTargetApi(input_0.target_identifier);
  }, []);
  // ── Impersonation context value ───────────────────────────────────────────
  const contextValue = useMemo(() => ({
    isActive: state.isActive,
    target: state.target,
    network: state.network,
    grantToken: state.grantToken,
    expiresAt: state.expiresAt,
    activate,
    stop,
    resolveTarget
  }), [state.isActive, state.target, state.network, state.grantToken, state.expiresAt, activate, stop, resolveTarget]);
  return _jsx(ImpersonationProvider, {
    isActive: state.isActive,
    onHydrate: onHydrate,
    startMonitor: startMonitor,
    children: _jsx(ImpersonationOverlayProvider, {
      value: overlay,
      children: _jsx(ImpersonationContext.Provider, {
        value: contextValue,
        children: children
      })
    })
  });
}